/* Shared protocol engine. Keep identical to mobile_node/src/offline/engine.js.
 * Storage.mutate must run a synchronous callback inside a durable transaction.
 * Network acknowledgements NEVER remove a newer local operation. */
export const emptyState = () => ({ schema: 1, packages: {}, operations: [], lease: null, lastError: null });
export const canonical = value => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return value.map(canonical);
  if (typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().filter(k => !['__proto__', 'constructor', 'prototype'].includes(k)).map(k => [k, canonical(value[k])]));
  return value;
};
export const equal = (a, b) => JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
export const pending = state => state.operations.filter(op => !['applied', 'abandoned'].includes(op.status));
export const validPackages = (state, now = Date.now()) => Object.values(state.packages).filter(pack => pack.manifest.expiresAt > now);
export function recordsFor(state, selectionKey) {
  const pack = state.packages[selectionKey];
  if (!pack) return [];
  const records = new Map(pack.records.map(record => [record.path, { ...record, values: { ...record.values }, syncStatus: 'synced' }]));
  for (const item of state.operations) {
    if ((item.selectionKey !== selectionKey && !records.has(item.op.path)) || item.status === 'abandoned' || item.status === 'applied') continue;
    const path = item.op.kind === 'create' ? `local:${item.op.id}` : item.op.path;
    const existing = records.get(path) || item.baseRecord;
    records.set(path, { ...existing, id: existing?.id || path, path, structureId: item.op.structureId,
      values: { ...(existing?.values || {}), ...item.op.changes }, dependencyId: item.op.dependencyId || existing?.dependencyId || null,
      syncStatus: item.status, operationId: item.op.id });
  }
  return [...records.values()];
}

function validateFieldValue(field, value) {
  if (value === undefined || (value !== null && !['string', 'number', 'boolean'].includes(typeof value))) throw new Error('INVALID_VALUE');
  if (typeof value === 'number' && !Number.isFinite(value)) throw new Error('INVALID_NUMBER');
  if (value !== null && value !== '' && ['number', 'integer'].includes(field.type) && (typeof value !== 'number' || (field.type === 'integer' && !Number.isInteger(value)))) throw new Error('INVALID_NUMBER');
  if (value !== null && field.type === 'boolean' && typeof value !== 'boolean') throw new Error('INVALID_BOOLEAN');
  if (value && ['date', 'date-time'].includes(field.type) && !Number.isFinite(Date.parse(value))) throw new Error('INVALID_DATE');
  if (value !== null && value !== '' && field.selections?.length && !field.selections.some(option => equal(option.value, value))) throw new Error('INVALID_SELECTION');
  if (field.required && (value === '' || value === null)) throw new Error('REQUIRED_FIELD');
}

export function createOfflineEngine({ storage, transport, uuid, hash, now = Date.now, onChange = () => {}, exclusive = fn => fn(), automaticRefresh = false }) {
  const owner = uuid();
  const read = () => storage.read();
  const mutate = async fn => { const result = await storage.mutate(fn); onChange(); return result; };
  const request = async (method, path, body) => {
    let response;
    try { response = await transport(method, `offlineV1${path}`, body); }
    catch (error) {
      if ([401, 403].includes(error.status)) await mutate(state => {
        for (const pack of Object.values(state.packages)) pack.manifest.expiresAt = 0;
      });
      throw error;
    }
    if (!response || response.errorData || response.error) {
      const error = new Error(response?.error?.code || response?.errorData?.message || 'NETWORK_UNAVAILABLE');
      error.status = response?.errorData?.status;
      throw error;
    }
    return response;
  };
  async function prepare(selection, progress = () => {}) {
    const startedAt = now();
    const manifest = await request('POST', '/prepare', selection);
    if (manifest.protocolVersion !== 1 || !manifest.ready || manifest.expiresAt <= now()) throw new Error('INVALID_PACKAGE');
    const records = [];
    for (const descriptor of manifest.pages) {
      const page = await request('GET', `/packages/${manifest.id}?page=${descriptor.index}`);
      if (page.id !== manifest.id || page.index !== descriptor.index || page.records.length !== descriptor.count ||
        await hash(JSON.stringify(canonical(page.records))) !== descriptor.hash) throw new Error('PACKAGE_INTEGRITY_FAILED');
      records.push(...page.records);
      progress(records.length, manifest.count);
    }
    if (records.length !== manifest.count || new Set(records.map(r => r.path)).size !== records.length) throw new Error('INCOMPLETE_PACKAGE');
    // Revalidate access/configuration after the last page. Never renew a lease
    // from the device clock or from an unsuccessful download.
    await request('GET', `/packages/${manifest.id}`);
    await mutate(state => {
      const updated = new Map(records.map(record => [record.path, record]));
      for (const item of state.operations) if (item.status === 'applied' && item.ackAt >= startedAt && item.result?.record &&
        (item.selectionKey === manifest.selectionKey || updated.has(item.result.record.path))) updated.set(item.result.record.path, item.result.record);
      state.packages[manifest.selectionKey] = { manifest, records: [...updated.values()] };
      state.lastError = null;
    });
    return manifest;
  }
  async function save(selectionKey, path, values, dependencyId) {
    let saved;
    await mutate(state => {
      const pack = state.packages[selectionKey];
      if (!pack || pack.manifest.expiresAt <= now()) throw new Error('OFFLINE_ACCESS_EXPIRED');
      const caps = pack.manifest.capabilities;
      const existing = path ? recordsFor(state, selectionKey).find(r => r.path === path) : null;
      const kind = path ? 'update' : 'create';
      if (!caps[kind] || existing?.locked || (path && !existing)) throw new Error('ACTION_ONLINE_ONLY');
      if (dependencyId !== undefined && (dependencyId || caps.relation)) values = { ...values, dependencyId: dependencyId || null };
      dependencyId = values.dependencyId;
      if (dependencyId && !caps.relation) throw new Error('RELATION_ONLINE_ONLY');
      const changes = {};
      for (const [key, value] of Object.entries(values)) {
        if (existing && equal(existing.values[key], value)) continue;
        const field = caps.fields.find(f => f.key === key && f.writable);
        if (!field) throw new Error('FIELD_ONLINE_ONLY');
        validateFieldValue(field, value);
        changes[key] = value;
      }
      if (!Object.keys(changes).length) return;
      if (kind === 'create') for (const field of caps.fields) if (field.required && (changes[field.key] === undefined || changes[field.key] === null || changes[field.key] === '')) throw new Error('REQUIRED_FIELD');
      const previous = path && [...state.operations].reverse().find(item => item.status !== 'abandoned' &&
        (item.op.path === path || (path && `local:${item.op.id}` === path)));
      const dependsOn = [...new Set([previous && previous.status !== 'applied' ? previous.op.id : null,
        path?.startsWith('local:') ? path.slice(6) : null, dependencyId?.startsWith('local:') ? dependencyId.slice(6) : null].filter(Boolean))];
      const operationId = uuid();
      const op = { id: operationId, kind, packageId: pack.manifest.id, structureId: pack.manifest.selection.structureId,
        structureVersion: pack.manifest.structureVersion, changes, baseValues: Object.fromEntries(Object.keys(changes).map(key => [key, existing?.values?.[key] ?? null])),
        dependsOn, ...(path ? { path } : {}), ...(dependencyId ? { dependencyId } : {}) };
      saved = { op, status: 'queued', selectionKey, baseRecord: existing || null, createdAt: now(), submitted: false };
      state.operations.push(saved);
    });
    return saved;
  }
  async function applyResult(operationId, result) {
    if (!['pending', 'applied', 'conflict', 'rejected'].includes(result.status)) throw new Error('INVALID_SYNC_RESULT');
    await mutate(state => {
      const item = state.operations.find(i => i.op.id === operationId);
      if (!item || item.status === 'abandoned') return;
      item.status = result.status; item.result = result.result; item.error = result.error;
      if (result.status !== 'applied') return;
      item.ackAt = now();
      const record = result.result?.record;
      if (!record?.path) throw new Error('INVALID_SYNC_RESULT');
      for (const [key, pack] of Object.entries(state.packages)) if (key === item.selectionKey || pack.records.some(r => r.path === record.path)) {
        pack.records = pack.records.filter(r => r.path !== record.path && r.path !== `local:${operationId}`);
        pack.records.push(record);
      }
      // Only unsent descendants may be rebased/assigned canonical paths. A
      // submitted payload is immutable because its ID is an idempotency key.
      for (const next of state.operations) if (!next.submitted && next.status === 'queued') {
        if (next.op.path === `local:${operationId}`) next.op.path = record.path;
        if (next.op.dependencyId === `local:${operationId}`) next.op.dependencyId = record.path;
        if (next.op.changes.dependencyId === `local:${operationId}`) next.op.changes.dependencyId = record.path;
        if (next.op.dependsOn.includes(operationId) && next.op.path === record.path) {
          // Preserve local edits while rebasing against server transformations.
          for (const key of Object.keys(next.op.baseValues)) if (equal(next.op.baseValues[key], item.op.changes[key])) next.op.baseValues[key] = record.values[key] ?? null;
        }
      }
    });
  }
  async function sync() {
    return exclusive(async () => {
      let acquired = false;
      await mutate(state => {
        if (state.lease && state.lease.owner !== owner && state.lease.expiresAt > now()) return;
        state.lease = { owner, expiresAt: now() + 120000 }; acquired = true;
      });
      if (!acquired) return;
      try {
        const snapshot = await read();
        let applied = false;
        for (const candidate of snapshot.operations) {
          if (!['queued', 'pending'].includes(candidate.status)) continue;
          const state = await read();
          const item = state.operations.find(i => i.op.id === candidate.op.id);
          if (!item || !['queued', 'pending'].includes(item.status)) continue;
          if (item.op.dependsOn.some(id => state.operations.find(i => i.op.id === id)?.status !== 'applied')) continue;
          await mutate(current => {
            if (current.lease?.owner !== owner) throw new Error('SYNC_LEASE_LOST');
            current.lease.expiresAt = now() + 120000;
            current.operations.find(i => i.op.id === item.op.id).submitted = true;
          });
          // Re-submit pending IDs too: repairs crashes after receipt persistence
          // but before Cloud Tasks enqueue, without changing their payload.
          const response = await request('POST', '/operations', { operations: [item.op] });
          let result = response.results?.find(r => r.id === item.op.id);
          if (!result) throw new Error('INVALID_SYNC_RESULT');
          if (result.status === 'pending') result = await request('GET', `/operations/${item.op.id}`);
          await applyResult(item.op.id, result);
          if (result.status === 'applied') applied = true;
        }
        if (automaticRefresh) {
          const current = await read();
          for (const pack of Object.values(current.packages)) {
            if (!applied && now() - pack.manifest.preparedAt < 300000) continue;
            if (!applied && now() - (current.lastRefreshAttempt || 0) < 30000) continue;
            await mutate(state => { state.lastRefreshAttempt = now(); });
            await prepare(pack.manifest.selection);
          }
        }
        await mutate(state => { state.lastError = null; });
      } catch (error) {
        await mutate(state => { state.lastError = error.message; });
        throw error;
      } finally {
        await mutate(state => { if (state.lease?.owner === owner) state.lease = null; });
      }
    });
  }
  async function resolve(operationId, values) {
    // A terminal rejection/conflict is resolved under a NEW ID. Descendants
    // remain blocked until the user reviews them; never silently re-parent.
    let replacement;
    await mutate(state => {
      const item = state.operations.find(i => i.op.id === operationId);
      if (!item || !['conflict', 'rejected'].includes(item.status)) throw new Error('NOT_RESOLVABLE');
      const pack = state.packages[item.selectionKey];
      if (!pack || pack.manifest.expiresAt <= now()) throw new Error('OFFLINE_ACCESS_EXPIRED');
      const server = item.error?.details?.record || pack.records.find(r => r.path === item.op.path);
      for (const [key, value] of Object.entries(values)) {
        const field = pack.manifest.capabilities.fields.find(f => f.key === key && f.writable);
        if (!field) throw new Error('FIELD_ONLINE_ONLY');
        validateFieldValue(field, value);
      }
      replacement = { ...item, op: { ...item.op, id: uuid(), changes: values, structureVersion: pack.manifest.structureVersion,
        packageId: pack.manifest.id, baseValues: Object.fromEntries(Object.keys(values).map(k => [k, server?.values?.[k] ?? null])) },
        status: 'queued', submitted: false, error: null, result: null, createdAt: now(), baseRecord: server || item.baseRecord };
      item.status = 'abandoned';
      if (server) {
        pack.records = pack.records.filter(record => record.path !== server.path);
        pack.records.push(server);
      }
      const replacementId = replacement.op.id;
      for (const next of state.operations) if (!next.submitted) {
        next.op.dependsOn = next.op.dependsOn.map(id => id === operationId ? replacementId : id);
        if (next.op.path === `local:${operationId}`) next.op.path = `local:${replacement.op.id}`;
        if (next.op.dependencyId === `local:${operationId}`) next.op.dependencyId = `local:${replacement.op.id}`;
        if (next.op.changes.dependencyId === `local:${operationId}`) next.op.changes.dependencyId = `local:${replacement.op.id}`;
      }
      const index = state.operations.indexOf(item);
      state.operations.splice(index + 1, 0, replacement);
    });
    return replacement;
  }
  async function removeSelection(selectionKey, abandon = false) {
    await mutate(state => {
      const outstanding = pending(state).filter(i => i.selectionKey === selectionKey);
      if (pending(state).some(item => item.selectionKey !== selectionKey && item.op.dependsOn.some(id => outstanding.some(parent => parent.op.id === id)))) throw new Error('DEPENDENT_CHANGES_EXIST');
      // Submitted operations may already be committed: they cannot be cancelled
      // by deleting local data. Reconcile them before removing the selection.
      if (outstanding.some(i => i.submitted && ['pending', 'queued'].includes(i.status))) throw new Error('SYNC_REQUIRED_BEFORE_REMOVAL');
      if (outstanding.length && !abandon) throw new Error('UNPUBLISHED_CHANGES');
      for (const item of outstanding) item.status = 'abandoned';
      delete state.packages[selectionKey];
    });
  }
  return { read, prepare, save, sync, resolve, removeSelection,
    exportChanges: async () => JSON.stringify({ protocolVersion: 1, exportedAt: now(), operations: pending(await read()) }, null, 2) };
}
